import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2 } from 'lucide-react';
import { useDeleteJobPayment } from '@/hooks/useJobPayments';
import { AddPaymentDialog } from './AddPaymentDialog';
import { useUserRole } from '@/hooks/useUserRole';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';
import { useTechUsers } from '@/hooks/useReferenceData';
import { Badge } from '@/components/ui/badge';
import { Smartphone } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useState } from 'react';

interface JobPayment {
  id: string;
  amount: number;
  payment_method: string;
  payment_date: string;
  notes?: string;
  recorded_by?: string | null;
}

interface JobPaymentsTableProps {
  payments: JobPayment[];
  jobId: string;
}

export function JobPaymentsTable({ payments, jobId }: JobPaymentsTableProps) {
  const { isAdmin, isCSR } = useUserRole();
  const { formatShortDate, formatMoney } = useCompanyLocale();
  const { data: techUsers } = useTechUsers();
  const deletePayment = useDeleteJobPayment();
  const [deletionReason, setDeletionReason] = useState('');
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(null);
  const [editingPayment, setEditingPayment] = useState<JobPayment | null>(null);

  if (payments.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        No payments recorded yet
      </div>
    );
  }

  return (
    <>
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Date</TableHead>
          <TableHead>Amount</TableHead>
          <TableHead>Method</TableHead>
          <TableHead>Notes</TableHead>
          {(isAdmin || isCSR) && <TableHead className="w-[120px]">Actions</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {payments.map((payment) => (
          <TableRow key={payment.id}>
            <TableCell>{formatShortDate(payment.payment_date)}</TableCell>
            <TableCell>{formatMoney(payment.amount)}</TableCell>
            <TableCell>
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="capitalize">
                  {payment.payment_method === 'square'
                    ? 'Square (online)'
                    : payment.payment_method === 'xero'
                      ? 'Recorded in Xero'
                      : payment.payment_method.replace('_', ' ')}
                </span>
                {!!payment.recorded_by && techUsers?.has(payment.recorded_by) && (
                  <Badge variant="outline" className="gap-1 text-[10px] px-1.5 py-0 text-info border-info/30" title={`Recorded on site by ${techUsers.get(payment.recorded_by)?.name ?? 'the technician'}`}>
                    <Smartphone className="h-3 w-3" />
                    Field
                  </Badge>
                )}
              </div>
            </TableCell>
            <TableCell>{payment.notes || '-'}</TableCell>
            {(isAdmin || isCSR) && (
              <TableCell className="flex gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={() => setEditingPayment(payment)}
                  aria-label="Edit payment"
                >
                  <Pencil className="h-4 w-4" />
                </Button>
                <AlertDialog
                  onOpenChange={(open) => {
                    if (open) {
                      setSelectedPaymentId(payment.id);
                    } else {
                      setDeletionReason('');
                      setSelectedPaymentId(null);
                    }
                  }}
                >
                  <AlertDialogTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Delete Payment</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to delete this payment of {formatMoney(payment.amount)}? This action cannot be undone and will be logged for audit purposes.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="py-4">
                      <Label htmlFor="deletion-reason">Reason for deletion *</Label>
                      <Textarea
                        id="deletion-reason"
                        placeholder="e.g., Duplicate entry, Incorrect amount, Customer dispute..."
                        value={deletionReason}
                        onChange={(e) => setDeletionReason(e.target.value)}
                        className="mt-2"
                        rows={3}
                      />
                    </div>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => {
                          deletePayment.mutate({ 
                            paymentId: payment.id, 
                            jobId,
                            reason: deletionReason 
                          });
                          setDeletionReason('');
                          setSelectedPaymentId(null);
                        }}
                        disabled={!deletionReason.trim()}
                        className="bg-destructive text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                      >
                        Delete
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </TableCell>
            )}
          </TableRow>
        ))}
      </TableBody>
    </Table>
    <AddPaymentDialog
      jobId={jobId}
      open={!!editingPayment}
      onOpenChange={(open) => !open && setEditingPayment(null)}
      payment={editingPayment}
    />
    </>
  );
}
